import { Link, Navigate, useParams } from "react-router";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { resourceGuides, type GuideBlock, type RichSection, type SimpleSection } from "../data/resourceGuides";
import { Seo } from "../components/Seo";
import { JsonLd } from "../components/JsonLd";

const SITE_ORIGIN = "https://www.deskreplay.com";

function isRichSection(s: SimpleSection | RichSection): s is RichSection {
  return "blocks" in s;
}

function RenderBlock({ block }: { block: GuideBlock }) {
  switch (block.type) {
    case "p":
      return <p className="text-on-dark-muted text-[15px] leading-relaxed">{block.text}</p>;

    case "bullets":
      return (
        <ul className="flex flex-col gap-2.5">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-2.5 text-on-dark-muted text-[15px] leading-relaxed">
              <CheckCircle2 size={15} className="text-teal-400 shrink-0 mt-[3px]" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );

    case "numbered":
      return (
        <ol className="flex flex-col gap-3">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-on-dark-muted text-[15px] leading-relaxed">
              <span className="shrink-0 w-6 h-6 rounded-full bg-teal-500/15 border border-teal-500/30 text-teal-400 text-xs font-bold flex items-center justify-center mt-[1px]">
                {i + 1}
              </span>
              <span>{item}</span>
            </li>
          ))}
        </ol>
      );

    case "callout":
      return (
        <div className="rounded-xl border border-teal-500/25 bg-teal-500/5 px-5 py-4">
          {block.label && (
            <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-1">{block.label}</p>
          )}
          <p className="text-slate-200 text-[15px] leading-relaxed font-medium">{block.text}</p>
        </div>
      );

    case "table":
      return (
        <div className="overflow-x-auto rounded-xl border border-slate-700">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-slate-800/80">
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-left text-slate-200 font-semibold text-xs uppercase tracking-wide border-b border-slate-700 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} className={ri % 2 === 0 ? "bg-slate-900/40" : "bg-slate-900/20"}>
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`px-4 py-3 text-[13px] leading-relaxed border-b border-slate-800 align-top ${
                        ci === 0 ? "text-slate-200 font-medium" : "text-on-dark-muted"
                      }`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export function ResourceGuide() {
  const { slug } = useParams<{ slug: string }>();
  const doc = slug ? resourceGuides[slug] : undefined;

  if (!doc) {
    return <Navigate to="/resources" replace />;
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: doc.title,
    description: doc.subtitle,
    url: `${SITE_ORIGIN}/resources/${slug ?? ""}`,
    publisher: {
      "@type": "Organization",
      name: "DeskReplay",
      logo: { "@type": "ImageObject", url: `${SITE_ORIGIN}/deskreplay.png` },
    },
    author: { "@type": "Organization", name: "DeskReplay" },
  };

  const faqSchema =
    doc.faq && doc.faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: doc.faq.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }
      : null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
      { "@type": "ListItem", position: 2, name: "Resources", item: `${SITE_ORIGIN}/resources` },
      { "@type": "ListItem", position: 3, name: doc.title, item: `${SITE_ORIGIN}/resources/${slug ?? ""}` },
    ],
  };

  return (
    <>
      <Seo
        title={doc.title}
        description={doc.subtitle}
        canonicalPath={`/resources/${slug ?? ""}`}
      />
      <JsonLd schema={articleSchema} id="article" />
      {faqSchema && <JsonLd schema={faqSchema} id="faq" />}
      <JsonLd schema={breadcrumbSchema} id="breadcrumb" />
      <div className="bg-slate-950 min-h-screen pt-28 pb-24">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm font-medium mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          All resources
        </Link>

        <header className="mb-5 pb-6 border-b border-slate-800">
          <p className="text-teal-400 text-xs uppercase tracking-widest font-semibold mb-2">DeskReplay resource</p>
          <h1 className="text-slate-100 text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">{doc.title}</h1>
          <p className="text-on-dark-muted text-lg leading-relaxed">{doc.subtitle}</p>
        </header>

        <div className="flex flex-col gap-10">
          {doc.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-slate-200 text-lg font-bold mb-5">{section.heading}</h2>
              {isRichSection(section) ? (
                <div className="flex flex-col gap-5">
                  {section.blocks.map((block, i) => (
                    <RenderBlock key={i} block={block} />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {section.body.map((p, i) => (
                    <p key={i} className="text-on-dark-muted text-[15px] leading-relaxed">
                      {p}
                    </p>
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
        </article>
      </div>
    </>
  );
}
